import React from 'react';

import { SettingsActionBarProvider } from '@apps/web/components/settings/settings_action_bar';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';

/**
 * Centered spinner shown while the settings gates (user profile / organization)
 * are still loading, so org section routes don't mount and gate-redirect against
 * transiently-false permissions on cold load.
 */
export function SettingsLoadingState(): React.JSX.Element {
    return (
        <div className="flex flex-1 items-center justify-center py-12">
            <Spinner size="xl" tone="muted" />
        </div>
    );
}

export type SettingsPageShellProps = {
    readonly title: string;
    readonly description?: string;
    readonly children: React.ReactNode;
    readonly maxWidth?: 'default' | 'wide';
    /** Rendered beside the page title, e.g. a "Beta" badge. */
    readonly titleBadge?: React.ReactNode;
    /** Rendered right-aligned on the header row, e.g. a page-level action. */
    readonly headerAction?: React.ReactNode;
    /** Rendered above the title, e.g. a back link on a nested settings page. */
    readonly eyebrow?: React.ReactNode;
};

export function SettingsPageShell({
    title,
    description,
    children,
    maxWidth = 'default',
    titleBadge,
    headerAction,
    eyebrow,
}: SettingsPageShellProps): React.JSX.Element {
    const contentClassName = cn(
        'mx-auto w-full px-4 sm:px-6 md:px-10',
        maxWidth === 'wide' ? 'max-w-7xl' : 'max-w-4xl',
    );

    return (
        <SettingsActionBarProvider portalClassName={contentClassName}>
            <div
                data-testid="settings-page-shell"
                className={cn(
                    contentClassName,
                    'shrink-0 space-y-9 py-6 md:py-12',
                )}
            >
                <div className="space-y-3">
                    {eyebrow}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                        <div className="min-w-0 space-y-1.5">
                            <div className="flex items-center gap-3">
                                <h1 className="text-foreground text-[28px] font-semibold tracking-tight">
                                    {title}
                                </h1>
                                {titleBadge}
                            </div>
                            {description !== undefined ? (
                                <p className="text-muted-foreground text-[15px]">
                                    {description}
                                </p>
                            ) : null}
                        </div>
                        {headerAction !== undefined ? (
                            <div className="shrink-0">{headerAction}</div>
                        ) : null}
                    </div>
                </div>
                {children}
            </div>
        </SettingsActionBarProvider>
    );
}

export function SettingsPageSections({
    children,
    className,
}: {
    readonly children: React.ReactNode;
    readonly className?: string;
}): React.JSX.Element {
    return <div className={cn('space-y-14', className)}>{children}</div>;
}

export function SettingsSectionHeader({
    title,
    description,
    titleBadge,
}: {
    readonly title: string;
    readonly description?: string;
    readonly titleBadge?: React.ReactNode;
}): React.JSX.Element {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-2">
                <h2 className="text-foreground text-xl font-semibold tracking-tight text-balance">
                    {title}
                </h2>
                {titleBadge}
            </div>
            {description !== undefined ? (
                <p className="text-muted-foreground max-w-3xl text-sm text-pretty">
                    {description}
                </p>
            ) : null}
        </div>
    );
}

export function SettingsPageSection({
    title,
    description,
    children,
    className,
}: {
    readonly title: string;
    readonly description?: string;
    readonly children: React.ReactNode;
    readonly className?: string;
}): React.JSX.Element {
    return (
        <section className={cn('space-y-5', className)}>
            <SettingsSectionHeader title={title} description={description} />
            {children}
        </section>
    );
}
